import type { PositionAnchorsState } from '../../types/layout';

/**
 * Valid position values for CSS positioning
 */
export const POSITION_VALUES = ['relative', 'absolute', 'fixed', 'sticky'] as const;
export type PositionValue = (typeof POSITION_VALUES)[number];

/**
 * Parse a position attribute value
 *
 * @param value - String value to parse (relative, absolute, fixed, sticky)
 * @returns Parsed position value or undefined if invalid
 */
export const parsePosition = (value: string | null | undefined): PositionValue | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (POSITION_VALUES.includes(normalized as PositionValue)) {
    return normalized as PositionValue;
  }

  return undefined;
};

/**
 * Parse a position offset value (top, left, right, bottom)
 * Only supports pixel values.
 *
 * @param value - String value to parse (e.g., "10", "10px")
 * @returns Parsed offset as CSS string (e.g., "10px") or undefined if not provided
 */
export const parsePositionOffset = (value: string | null | undefined): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const trimmed = value.trim();
  if (trimmed === '') {
    return undefined;
  }

  // Strip 'px' suffix if present, then parse as integer
  const numStr = trimmed.endsWith('px') ? trimmed.slice(0, -2) : trimmed;
  const num = parseInt(numStr, 10);

  if (isNaN(num)) {
    return undefined;
  }

  return `${num}px`;
};

/**
 * Infer positionAnchorsState from provided position values.
 *
 * Rules:
 * - If position is 'absolute' or 'fixed':
 *   - enabled = true
 *   - enableTop = true if top is provided
 *   - enableLeft = true if left is provided
 *   - enableRight = true if right is provided
 *   - enableBottom = true if bottom is provided
 *   - If neither vertical anchor is set, default enableTop = true
 *   - If neither horizontal anchor is set, default enableLeft = true
 * - Otherwise: all anchors disabled (default state)
 *
 * @param position - Position type
 * @param top - Top offset value
 * @param left - Left offset value
 * @param right - Right offset value
 * @param bottom - Bottom offset value
 * @returns Inferred PositionAnchorsState
 */
export const inferPositionAnchorsState = (
  position: PositionValue | undefined,
  top: string | undefined,
  left: string | undefined,
  right: string | undefined,
  bottom: string | undefined,
): PositionAnchorsState => {
  // Default disabled state
  const defaultState: PositionAnchorsState = {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  };

  // Only compute anchors for absolute or fixed positioning
  if (position !== 'absolute' && position !== 'fixed') {
    return defaultState;
  }

  // Determine which anchors have values
  const hasTop = top !== undefined;
  const hasLeft = left !== undefined;
  const hasRight = right !== undefined;
  const hasBottom = bottom !== undefined;

  // Determine vertical anchor (default to top if neither provided)
  let enableTop = hasTop;
  const enableBottom = hasBottom;
  if (!hasTop && !hasBottom) {
    enableTop = true;
  }

  // Determine horizontal anchor (default to left if neither provided)
  let enableLeft = hasLeft;
  const enableRight = hasRight;
  if (!hasLeft && !hasRight) {
    enableLeft = true;
  }

  return {
    enableTop,
    enableRight,
    enableBottom,
    enableLeft,
    enabled: true,
  };
};

/**
 * Position-related attributes for elements
 */
export const POSITION_ATTRIBUTES = [
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
] as const;

/**
 * Position-related breakpoint attributes
 */
export const POSITION_BREAKPOINT_ATTRIBUTES = [
  'tablet-position',
  'tablet-top',
  'tablet-left',
  'tablet-right',
  'tablet-bottom',
  'tablet-zIndex',
  'mobile-position',
  'mobile-top',
  'mobile-left',
  'mobile-right',
  'mobile-bottom',
  'mobile-zIndex',
] as const;

/**
 * All position-related attributes including breakpoints
 */
export const ALL_POSITION_ATTRIBUTES = [
  ...POSITION_ATTRIBUTES,
  ...POSITION_BREAKPOINT_ATTRIBUTES,
] as const;

/**
 * Check if an attribute is a position-related attribute
 */
export const isPositionAttribute = (attr: string): boolean => {
  return (ALL_POSITION_ATTRIBUTES as readonly string[]).includes(attr);
};
